import { useEffect, useRef, useState } from 'react';
import { UploadSimple } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';
import { UPLOAD_BUTTON_TOOLTIP, UPLOAD_DIALOG_DESCRIPTION, UPLOAD_DIALOG_TITLE } from './pro-builder-copy';
import { useProBuilderUploadStore, type UploadRow } from './pro-builder-upload-store';
import { handleWindowFileDrag, proBuilderDropHandlers } from './pro-builder-drop';
import { focusProBuilderTerminal } from './pro-builder-publish-dialog';
import { publishDialogOpen } from './pro-builder-window-rules';

/** The header's Upload button, shown over the terminal, where an app (and so its folder) is bound. */
export function ProBuilderUploadButton() {
  const openDialog = useProBuilderUploadStore((s) => s.openDialog);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={openDialog}
        aria-label="Upload documents"
        data-testid="pro-builder-upload"
        tooltip={UPLOAD_BUTTON_TOOLTIP}
        className="h-[34px] shrink-0 gap-2 rounded-[10px] px-2.5 text-[length:var(--text-sm)] font-medium text-muted-foreground hover:text-foreground"
      >
        <UploadSimple size={16} />
        Upload
      </Button>
    </>
  );
}

function RowView({ row }: { row: UploadRow }) {
  return (
    <li data-testid="pro-builder-upload-row" data-file={row.name} data-state={row.state} className="flex flex-col gap-1 rounded-md border border-border p-2">
      <span className="truncate text-sm font-medium text-foreground">{row.name}</span>
      <div
        role="progressbar"
        aria-label={`Upload progress for ${row.name}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={row.progress}
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn('h-full transition-[width]', row.state === 'failed' ? 'bg-destructive' : row.state === 'done' ? 'bg-success' : 'bg-primary')}
          style={{ width: `${row.progress}%` }}
        />
      </div>
      <span
        data-testid="pro-builder-upload-row-detail"
        className={cn('text-xs', row.state === 'failed' ? 'text-destructive' : row.state === 'done' ? 'text-success' : 'text-muted-foreground')}
      >
        {row.detail}
      </span>
    </li>
  );
}

/** The upload dialog: pick or drop files, watch one row per file. Mounted once, by the header, over the terminal view. */
export function ProBuilderUploadDialog({ folder, windowOpen }: { folder: string | null; windowOpen: boolean }) {
  const open = useProBuilderUploadStore((s) => s.open);
  const allRows = useProBuilderUploadStore((s) => s.rows);
  // Only this app's uploads: rows sent while another app was open stay with that app.
  const rows = allRows.filter((row) => row.folder === folder);
  const closeDialog = useProBuilderUploadStore((s) => s.closeDialog);
  const enqueue = useProBuilderUploadStore((s) => s.enqueue);
  const inputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const take = (files: FileList | null | undefined) => {
    if (!folder || !files || files.length === 0) return;
    enqueue(Array.from(files), folder);
  };
  const drop = proBuilderDropHandlers({ folder, depth, setOver, openDialog: false });
  const dialogOpen = publishDialogOpen(open, windowOpen);
  // The dimmed backdrop is no drop target, and nothing else cancels `dragover`, so while the dialog is open
  // a file released beside the box would open in the tab. Bubble phase: the box's own handler runs first.
  useEffect(() => {
    if (!dialogOpen) return;
    const onWindowDrag = (e: DragEvent) => handleWindowFileDrag(e, folder, enqueue);
    window.addEventListener('dragover', onWindowDrag);
    window.addEventListener('drop', onWindowDrag);
    return () => {
      window.removeEventListener('dragover', onWindowDrag);
      window.removeEventListener('drop', onWindowDrag);
    };
  }, [dialogOpen, folder, enqueue]);
  return (
    <Dialog open={dialogOpen} onOpenChange={(next) => !next && closeDialog()}>
      <DialogContent
        data-testid="pro-builder-upload-dialog"
        // The whole dialog takes a drop, so a file released outside the dashed box is queued, never opened by the browser.
        {...drop}
        className="sm:max-w-lg"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          focusProBuilderTerminal();
        }}
      >
        <DialogHeader>
          <DialogTitle>{UPLOAD_DIALOG_TITLE}</DialogTitle>
          <DialogDescription>{UPLOAD_DIALOG_DESCRIPTION}</DialogDescription>
        </DialogHeader>
        <div
          data-testid="pro-builder-upload-dropzone"
          className={cn(
            'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center',
            over ? 'border-primary bg-primary/5' : 'border-border',
          )}
        >
          <UploadSimple size={20} className="text-muted-foreground" />
          <span className="text-sm text-foreground">Drop files here</span>
          <Button type="button" variant="outline" size="sm" data-testid="pro-builder-upload-choose" onClick={() => inputRef.current?.click()}>
            Choose files
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            data-testid="pro-builder-upload-input"
            onChange={(e) => {
              take(e.currentTarget.files);
              // So the same file can be picked twice in a row.
              e.currentTarget.value = '';
            }}
          />
        </div>
        {rows.length > 0 && (
          <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto" aria-label="Uploads">
            {rows.map((row) => (
              <RowView key={row.id} row={row} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
